import { useContext, useEffect, useLayoutEffect, useRef, useState, FunctionComponent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { unique } from 'remeda';

import { getPurchasesPackages, presentWebPaywall } from 'src/modules/purchase/RevenueCat';
import { logPaywallConversion } from 'src/modules/purchase/logPaywallConversion';
import { RevenueCatPlacement } from 'src/modules/purchase/purchase.constants';
import { logAnalyticsEvent, trackPaywallError } from 'src/modules/analytics/analytics';
import { reportError } from 'src/modules/logs/Sentry';
import { PortalContainerContext } from 'src/contexts/PortalContainerContext';
import Button from 'src/designSystem/Button';

const USER_CANCELLED_ERROR_CODE = 1;
const isUserCancelledError = (error: unknown): boolean =>
  (error as { errorCode?: number } | null)?.errorCode === USER_CANCELLED_ERROR_CODE;

type Props = {
  isOpen: boolean;
  host: 'inline' | 'overlay';
  placement: RevenueCatPlacement;
  isBlocking?: boolean;
  shouldLogPaywallEvents?: boolean;
  onPurchased: () => Promise<void> | void;
  onDismiss: (didAttemptPurchase: boolean) => void;
};

const WebPaywallTrigger: FunctionComponent<Props> = ({
  isOpen,
  host,
  placement,
  isBlocking = false,
  shouldLogPaywallEvents = false,
  onPurchased,
  onDismiss,
}) => {
  const { t, i18n } = useTranslation();
  const portalContainer = useContext(PortalContainerContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const attemptedRef = useRef(false);
  const dismissedRef = useRef(false);
  const onPurchasedRef = useRef(onPurchased);
  const onDismissRef = useRef(onDismiss);
  const shouldLogPaywallEventsRef = useRef(shouldLogPaywallEvents);
  const isBlockingRef = useRef(isBlocking);
  const languageRef = useRef(i18n.language);
  const [presentationState, setPresentationState] = useState<'idle' | 'presenting' | 'error'>('idle');

  useEffect(() => {
    onPurchasedRef.current = onPurchased;
    onDismissRef.current = onDismiss;
    shouldLogPaywallEventsRef.current = shouldLogPaywallEvents;
    isBlockingRef.current = isBlocking;
    languageRef.current = i18n.language;
  });

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPresentationState(isOpen ? 'presenting' : 'idle');
  }, [isOpen, host, placement]);

  useEffect(() => {
    if (presentationState !== 'presenting') return;
    if (!containerRef.current) return;
    attemptedRef.current = false;
    dismissedRef.current = false;
    let cancelled = false;
    const callDismiss = (didAttemptPurchase: boolean) => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      onDismissRef.current(didAttemptPurchase);
    };
    const failPresentation = () => {
      if (isBlockingRef.current) {
        setPresentationState('error');
        return;
      }
      callDismiss(attemptedRef.current);
    };

    if (shouldLogPaywallEventsRef.current) {
      logAnalyticsEvent('trial_ended');
      void (async () => {
        try {
          const packages = await getPurchasesPackages(placement);
          if (cancelled) return;
          logAnalyticsEvent('paywall_viewed', {
            type: 'blocking_trial_ended',
            placement,
            offering_id: packages[0]?.offeringIdentifier ?? null,
            package_count: packages.length,
            package_types: unique(packages.map(p => p.packageType)),
            intro_price_package_types: unique(
              packages.filter(p => !!p.product.introPrice).map(p => p.packageType),
            ),
            product_ids: packages.map(p => p.product.identifier),
          });
        } catch (error) {
          reportError('Failed to fetch paywall packages for analytics', error);
          if (cancelled) return;
          logAnalyticsEvent('paywall_viewed', {
            type: 'blocking_trial_ended',
            placement,
          });
        }
      })();
    }

    const selectedLocale = languageRef.current;

    presentWebPaywall({
      placement,
      htmlTarget: containerRef.current ?? undefined,
      selectedLocale,
      onBack: closePaywall => {
        closePaywall();
        if (cancelled) return;
        callDismiss(attemptedRef.current);
      },
      onPurchaseCancelled: () => {
        attemptedRef.current = true;
      },
      onPurchaseError: error => {
        attemptedRef.current = true;
        if (cancelled) return;
        trackPaywallError({
          operation: 'purchase',
          error_code: String(
            (error as { errorCode?: number }).errorCode ?? error.name,
          ),
          error_message: error.message,
          placement,
        });
      },
    })
      .then(async result => {
        if (cancelled) return;
        if (!result) {
          reportError('Web paywall unavailable (RC web SDK not ready)');
          failPresentation();
          return;
        }
        logPaywallConversion(
          result.productIdentifier,
          placement,
          result.customerInfo ?? undefined,
        );
        dismissedRef.current = true;
        try {
          await onPurchasedRef.current();
        } catch {
          // onPurchased owns reporting/handling of its own failures.
        }
      })
      .catch(error => {
        if (cancelled) return;
        if (isUserCancelledError(error)) {
          callDismiss(attemptedRef.current);
          return;
        }
        reportError('Web paywall error', error);
        failPresentation();
      });

    return () => {
      cancelled = true;
    };
  }, [presentationState, placement]);

  const handleRetry = () => {
    setPresentationState('presenting');
  };

  if (!isOpen || presentationState === 'idle') return null;

  if (presentationState === 'error') {
    const errorNode = (
      <div
        data-testid="web-paywall-error"
        className={
          host === 'inline'
            ? 'flex flex-col items-center justify-center gap-4 w-full h-full p-6 text-center'
            : 'fixed inset-y-0 fixed-in-column z-takeover flex flex-col items-center justify-center gap-4 bg-primary-darkest p-6 text-center'
        }
      >
        <div className="text-base font-semibold text-structural-lightest">
          {t('common.error')}
        </div>
        <Button
          type="default"
          onClick={handleRetry}
          data-testid="btn-web-paywall-retry"
        >
          {t('global.restart')}
        </Button>
      </div>
    );
    return host === 'inline'
      ? errorNode
      : createPortal(errorNode, portalContainer || document.body);
  }

  if (host === 'inline') {
    return (
      <div
        ref={containerRef}
        data-testid="web-paywall-container"
        className="w-full h-full"
      />
    );
  }

  return createPortal(
    <div
      ref={containerRef}
      data-testid="web-paywall-container"
      className="fixed inset-y-0 fixed-in-column z-takeover overflow-y-auto"
    />,
    portalContainer || document.body,
  );
};

export default WebPaywallTrigger;
